import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, UserPlus } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const ROLES = ["owner", "manager", "staff", "analyst"];

function AddedCard({ result, orgName, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(result.temporary_password); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="rounded-2xl bg-[#0B0D10] text-white p-6 mb-6 max-w-xl" data-testid="team-added-card">
      <div className="flex items-center gap-2 text-[#C8FF4D] text-[11px] uppercase tracking-[0.16em] font-semibold"><Check size={13} strokeWidth={2.5} /> {result.created_user ? "Login created" : "Added to team"}</div>
      <div className="font-display font-bold text-lg mt-2">{result.name} is now {result.role === "owner" ? "an owner" : `a ${result.role}`} at {orgName}.</div>
      {result.created_user ? (
        <div className="mt-4 grid gap-3 text-sm">
          <div><div className="text-white/50 text-xs mb-1">Email</div><div className="font-medium" data-testid="team-added-email">{result.email}</div></div>
          <div>
            <div className="text-white/50 text-xs mb-1">Temporary password</div>
            <div className="flex items-center gap-2">
              <code className="font-mono bg-white/10 px-3 py-1.5 rounded-lg" data-testid="team-added-password">{result.temporary_password}</code>
              <button onClick={copy} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white" data-testid="team-copy-password">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}</button>
            </div>
          </div>
          <p className="text-white/55 text-xs leading-relaxed">{result.email_sent ? "We emailed these to them too." : "Hand these over now: this password is only shown once."} They can change it under Settings → Account.</p>
        </div>
      ) : (
        <p className="mt-3 text-white/60 text-sm">They already had a BackPocket login, so they'll see {orgName} in their business switcher next time they sign in.</p>
      )}
      <button onClick={onDismiss} className="mt-4 text-xs text-white/50 hover:text-white underline" data-testid="team-added-dismiss">Done</button>
    </div>
  );
}

export default function Team() {
  const { activeOrg, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "staff", send_welcome_email: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const canAssignOwner = user?.is_superadmin || members.some((m) => m.email === user?.email && m.role === "owner");
  const roles = canAssignOwner ? ROLES : ROLES.filter((r) => r !== "owner");
  const load = () => activeOrg && api.get(`/orgs/${activeOrg.id}/team`).then((r) => setMembers(r.data.members || []));
  useEffect(() => { load(); }, [activeOrg]);
  if (!activeOrg) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${activeOrg.id}/team/members`, form);
      setResult(data);
      setForm({ name: "", email: "", role: "staff", send_welcome_email: true });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader title="Team" subtitle="Add teammates and assign roles. We create their login for them." />
      {result && <AddedCard result={result} orgName={activeOrg.name} onDismiss={() => setResult(null)} />}
      <div className="rounded-2xl bg-white border border-[#E8E5DE] p-6 mb-6 max-w-xl" data-testid="team-add-form">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[#3a3d44]/60 font-semibold mb-4"><UserPlus size={13} /> Add a team member</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input className="bg-white" placeholder="Full name" value={form.name} onChange={set("name")} data-testid="team-name" />
          <Input className="bg-white" type="email" placeholder="teammate@business.com.au" value={form.email} onChange={set("email")} data-testid="team-email" />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <select className="h-10 rounded-md border border-[#E8E5DE] bg-white px-3 text-sm capitalize" value={form.role} onChange={set("role")} data-testid="team-role">{roles.map((r) => <option key={r} value={r}>{r}</option>)}</select>
          <label className="flex items-center gap-2 text-sm text-[#3a3d44]"><input type="checkbox" checked={form.send_welcome_email} onChange={set("send_welcome_email")} data-testid="team-send-email" /> Email them their login</label>
          <Button onClick={add} disabled={!form.email || busy} className="bg-[#0B0D10] text-white rounded-full ml-auto" data-testid="team-add-btn">{busy ? "Adding…" : "Add to team"}</Button>
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-[#E8E5DE] overflow-hidden" data-testid="team-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F3EC] text-left text-xs uppercase tracking-wider text-[#25282E]"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th></tr></thead>
          <tbody>
            {members.map((m) => <tr key={m.user_id} className="border-t border-[#F0EDE5]" data-testid={`team-row-${m.user_id}`}><td className="p-3 font-medium">{m.name}</td><td className="p-3">{m.email}</td><td className="p-3 capitalize">{m.role}</td></tr>)}
            {members.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-[#3a3d44]/50">No team members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
